import { uploadFile } from '@/utils/uploadFile';
import { ServiceEvidenceImage } from './ServiceEvidenceImage';
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Loader2, Camera, CheckCircle, FileText, UserCheck,
  Wrench, Package, Clock, Upload, X,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatDate } from '@/utils/formatters';

interface PartsUsed {
  partName: string;
  quantity: string;
  cost: string;
  partNumber: string;
}

export function OnSiteExecutionPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    workDescription: '',
    issueExplainedToCustomer: false,
    repairType: 'repair' as 'repair' | 'replace',
    workCompleted: false,
    chargeable: false,
    customerApproval: false,
    systemTestResult: 'pass' as 'pass' | 'fail',
    testTemperature: '',
    testLeakage: false,
  });

  const [uploadError, setUploadError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [beforePhotos, setBeforePhotos] = useState<string[]>([]);
  const [afterPhotos, setAfterPhotos] = useState<string[]>([]);
  const [partsUsed, setPartsUsed] = useState<PartsUsed[]>([]);
  const [newPart, setNewPart] = useState({ partName: '', quantity: '', cost: '', partNumber: '' });
  const [timeIn, setTimeIn] = useState('');
  const [timeOut, setTimeOut] = useState('');
  const [customerSignature, setCustomerSignature] = useState('');

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>, target: 'before' | 'after') => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!id) return;
    setUploadError('');setUploading(true);
    try {
      const file = await uploadFile(e.target.files![0], {entity:'service_ticket',entityId:id});
      const url = `/api/v1/files/${file.id}/download`;
      if (target === 'before') setBeforePhotos(p => [...p,url]);
      else setAfterPhotos(p => [...p,url]);
    } catch { setUploadError('Could not upload photo. Please retry.'); }
    finally { setUploading(false); }

  };

  const addPart = () => {
    if (!newPart.partName || !newPart.quantity.trim() || !Number.isFinite(Number(newPart.quantity)) || Number(newPart.quantity)<=0 || !newPart.cost.trim() || !Number.isFinite(Number(newPart.cost)) || Number(newPart.cost)<0) return;
    setPartsUsed((p) => [...p, newPart]);
    setNewPart({ partName: '', quantity: '', cost: '', partNumber: '' });
  };

  const removePart = (idx: number) => {
    setPartsUsed((p) => p.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!id) return;
    if (!form.workCompleted) {
      alert('Please mark work as completed');
      return;
    }
    if (form.chargeable && !form.customerApproval) {
      alert('Customer approval required for chargeable work');
      return;
    }
    if (beforePhotos.length === 0 || afterPhotos.length === 0) {
      alert('Before and after photos are mandatory');
      return;
    }

    setSubmitting(true);
    try {
      await api.patch(`/service-sop/complaints/${id}/on-site`, {
        issueExplainedToCustomer: form.issueExplainedToCustomer,
        isChargeable: form.chargeable,
        customerApprovalForCharge: form.customerApproval,
        repairType: form.repairType,
        repairLog: form.workDescription,
        systemTestResult: form.systemTestResult,
        beforePhotos,
        afterPhotos,
        partsUsed: partsUsed.map(p=>({name:p.partName,qty:Number(p.quantity),cost:Number(p.cost)})),
        ...(timeIn ? {timeIn:new Date(timeIn).toISOString()} : {}),
        ...(timeOut ? {timeOut:new Date(timeOut).toISOString()} : {}),
        customerSignature,
      });
      navigate(`/service-tickets/${id}`);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save execution');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <label><input type="checkbox" checked={form.issueExplainedToCustomer} onChange={e=>setForm({...form,issueExplainedToCustomer:e.target.checked})}/>Issue explained to customer</label>
      <label>Work type<select value={form.repairType} onChange={e=>setForm({...form,repairType:e.target.value as 'repair'|'replace'})}><option value="repair">Repair</option><option value="replace">Replace</option></select></label>
      {uploadError && <p role="alert">{uploadError}</p>}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[var(--text-primary)]">On-Site Execution</h1>
        <p className="text-sm text-[var(--text-muted)]">Record repair/replacement details</p>
      </div>

      <div className="space-y-6">
        {/* Work Description */}
        <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
          <h3 className="font-semibold text-[var(--text-primary)] mb-4 flex items-center gap-2">
            <Wrench size={18} className="text-[var(--ink-0)]" />
            Work Description
          </h3>
          <textarea
            value={form.workDescription}
            onChange={(e) => setForm((f) => ({ ...f, workDescription: e.target.value }))}
            rows={4}
            placeholder="Describe the work performed..."
            className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--ink-0)] focus:outline-none"
          />

          <label className="mt-4 flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.workCompleted}
              onChange={(e) => setForm((f) => ({ ...f, workCompleted: e.target.checked }))}
              className="h-5 w-5 rounded border-gray-300 text-[var(--ink-0)]"
            />
            <span className="font-medium text-[var(--text-primary)]">Work Completed</span>
          </label>
        </div>

        {/* Chargeable Work */}
        <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
          <label className="flex items-center gap-3 cursor-pointer mb-4">
            <input
              type="checkbox"
              checked={form.chargeable}
              onChange={(e) => setForm((f) => ({ ...f, chargeable: e.target.checked }))}
              className="h-5 w-5 rounded border-gray-300 text-[var(--ink-0)]"
            />
            <span className="font-medium text-[var(--text-primary)]">Chargeable Service</span>
          </label>

          {form.chargeable && (
            <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.customerApproval}
                  onChange={(e) => setForm((f) => ({ ...f, customerApproval: e.target.checked }))}
                  className="h-5 w-5 rounded border-gray-300 text-[var(--ink-0)]"
                />
                <span className="font-medium text-amber-800">Customer Approved Chargeable Work</span>
              </label>
            </div>
          )}
        </div>

        {/* Before Photos */}
        <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
          <h3 className="font-semibold text-[var(--text-primary)] mb-4 flex items-center gap-2">
            <Camera size={18} className="text-red-500" />
            Before Photos <span className="text-red-500">*</span>
          </h3>
          <div className="flex flex-wrap gap-2">
            {beforePhotos.map((url, idx) => (
              <div key={idx} className="relative">
                <ServiceEvidenceImage url={url} alt="Service evidence" className="h-24 w-24 rounded-lg object-cover border"/>
                <button
                  type="button"
                  onClick={() => setBeforePhotos((p) => p.filter((_, i) => i !== idx))}
                  className="absolute -top-2 -right-2 rounded-full bg-red-500 p-1 text-white hover:bg-red-600"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
            <label className="flex h-24 w-24 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-red-300 hover:bg-red-50">
              <input type="file" accept="image/*" onChange={(e) => handlePhotoUpload(e, 'before')} className="hidden" />
              <Camera size={20} className="text-red-400" />
            </label>
          </div>
        </div>

        {/* After Photos */}
        <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
          <h3 className="font-semibold text-[var(--text-primary)] mb-4 flex items-center gap-2">
            <Camera size={18} className="text-green-500" />
            After Photos <span className="text-green-500">*</span>
          </h3>
          <div className="flex flex-wrap gap-2">
            {afterPhotos.map((url, idx) => (
              <div key={idx} className="relative">
                <ServiceEvidenceImage url={url} alt="Service evidence" className="h-24 w-24 rounded-lg object-cover border"/>
                <button
                  type="button"
                  onClick={() => setAfterPhotos((p) => p.filter((_, i) => i !== idx))}
                  className="absolute -top-2 -right-2 rounded-full bg-red-500 p-1 text-white hover:bg-red-600"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
            <label className="flex h-24 w-24 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-green-300 hover:bg-green-50">
              <input type="file" accept="image/*" onChange={(e) => handlePhotoUpload(e, 'after')} className="hidden" />
              <Camera size={20} className="text-green-400" />
            </label>
          </div>
        </div>

        {/* Parts Used */}
        <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
          <h3 className="font-semibold text-[var(--text-primary)] mb-4 flex items-center gap-2">
            <Package size={18} className="text-[var(--ink-0)]" />
            Parts Used
          </h3>
          <div className="space-y-3 mb-4">
            {partsUsed.map((part, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-[var(--bg-muted)]">
                <div>
                  <span className="font-medium text-[var(--text-primary)]">{part.partName}</span>
                  <span className="text-xs text-[var(--text-muted)] ml-2">x{part.quantity}</span>
                  {part.partNumber && <span className="text-xs text-[var(--text-muted)] ml-2">({part.partNumber})</span>}
                </div>
                <button onClick={() => removePart(idx)} className="text-red-500 hover:text-red-700">×</button>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-4 gap-3">
            <input aria-label="Part cost" type="number" value={newPart.cost} onChange={e=>setNewPart(p=>({...p,cost:e.target.value}))} placeholder="Cost"/>
            <input
              type="text"
              value={newPart.partName}
              onChange={(e) => setNewPart((p) => ({ ...p, partName: e.target.value }))}
              placeholder="Part name"
              className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm"
            />
            <input
              type="number"
              value={newPart.quantity}
              onChange={(e) => setNewPart((p) => ({ ...p, quantity: e.target.value }))}
              placeholder="Qty"
              className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm"
            />
            <input
              type="text"
              value={newPart.partNumber}
              onChange={(e) => setNewPart((p) => ({ ...p, partNumber: e.target.value }))}
              placeholder="Part #"
              className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm"
            />
          </div>
          <button onClick={addPart} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">+ Add Part</button>
        </div>

        {/* Time Logging */}
        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
            <h3 className="font-semibold text-[var(--text-primary)] mb-2 flex items-center gap-2">
              <Clock size={16} /> Time In
            </h3>
            <input
              type="datetime-local"
              value={timeIn}
              onChange={(e) => setTimeIn(e.target.value)}
              className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm"
            />
          </div>
          <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
            <h3 className="font-semibold text-[var(--text-primary)] mb-2 flex items-center gap-2">
              <Clock size={16} /> Time Out
            </h3>
            <input
              type="datetime-local"
              value={timeOut}
              onChange={(e) => setTimeOut(e.target.value)}
              className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm"
            />
          </div>
        </div>

        {/* System Test */}
        <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
          <h3 className="font-semibold text-[var(--text-primary)] mb-4">System Test Result</h3>
          <div className="flex gap-4 mb-4">
            {(['pass', 'fail'] as const).map((result) => (
              <button
                key={result}
                onClick={() => setForm((f) => ({ ...f, systemTestResult: result }))}
                className={`flex-1 rounded-lg border px-4 py-3 text-sm font-medium transition-colors ${
                  form.systemTestResult === result
                    ? result === 'pass' ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-red-500 bg-red-50 text-red-700'
                    : 'border-[var(--border-primary)] text-[var(--text-secondary)]'
                }`}
              >
                {result === 'pass' ? '✓ Pass' : '✗ Fail'}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-[var(--text-muted)]">Final Temperature (°C)</label>
              <input
                type="text"
                value={form.testTemperature}
                onChange={(e) => setForm((f) => ({ ...f, testTemperature: e.target.value }))}
                placeholder="e.g. 55°C"
                className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm"
              />
            </div>
            <div className="flex items-center gap-3 pt-6">
              <input
                type="checkbox"
                checked={form.testLeakage}
                onChange={(e) => setForm((f) => ({ ...f, testLeakage: e.target.checked }))}
                className="h-5 w-5 rounded border-gray-300 text-[var(--ink-0)]"
              />
              <span className="text-sm text-[var(--text-secondary)]">No Leakage Detected</span>
            </div>
          </div>
        </div>

        {/* Customer Signature */}
        <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
          <h3 className="font-semibold text-[var(--text-primary)] mb-4 flex items-center gap-2">
            <UserCheck size={18} className="text-[var(--ink-0)]" />
            Customer Signature / Approval
          </h3>
          <input
            type="text"
            value={customerSignature}
            onChange={(e) => setCustomerSignature(e.target.value)}
            placeholder="Customer name or signature"
            className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm"
          />
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-3">
          <button
            onClick={() => navigate(`/service-tickets/${id}`)}
            className="rounded-lg border border-[var(--border-primary)] px-4 py-2 text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-muted)]"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !form.workCompleted}
            className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
            Complete & Close
          </button>
        </div>
      </div>
    </div>
  );
}